import * as React from 'react';
import { usePerformAction, useDataLoader } from 'react-remote-data-hooks';
import classnames from 'classnames';
import { useStateWithCallbackLazy } from 'use-state-with-callback';
import api from '../../../../api';
import {
  NotificationSettings,
  convertPascalCaseToWords,
  ITemplateKeyItem,
  AppNotificationSettings,
} from '../../../../core';
import { useAppSettings } from '../../../../core/hooks/useAppSettings';
import UpdateForm from './UpdateForm';

interface IQuotationSettingsProps {
  data?: NotificationSettings;
  reload?: () => void;
}

const AppNotificationSettingsTab: React.FunctionComponent<IQuotationSettingsProps> = (
  props
) => {
  const appSettings = useAppSettings(api.settings);

  const [editModalOpen, setEditModalOpen] = useStateWithCallbackLazy<{
    isOpen?: boolean;
    event?: ITemplateKeyItem;
    data?: AppNotificationSettings;
  }>({});

  const {
    performAction: update,
    error: updateError,
    isPerforming: updating,
  } = usePerformAction(
    (data: AppNotificationSettings[]) =>
      api.settings.updateAppNotificationSettings(data),
    () => {
      setEditModalOpen({}, () => {
        props.reload?.();
      });
      appSettings.reload();
    }
  );

  const { data: events } = useDataLoader<ITemplateKeyItem[]>(() =>
    api.settings.getEventKeys()
  );

  return (
    <>
      <div className="col-lg-4 col-md-6 col-12 border-right">
        <div className="d-flex align-items-center py-3">
          <div className="col">
            <h4 className="mb-0 font-weight-semibold">
              App Notification Settings
            </h4>
          </div>
        </div>
        <div className="grid-table grid-table--sm border-top">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="d-inline-flex align-items-center">
                    Events
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {events
                ?.filter((e) => !!e.appNotificationTemplateOptions)
                ?.sort((a, b) => (a.key <= b.key ? -1 : 1))
                ?.map((item) => {
                  const addedSettings = props.data?.appNotifications?.find(
                    (x) => x.event === item?.key
                  );
                  return (
                    <tr
                      className={classnames({
                        'cursor-pointer': true,
                        'text-blue': editModalOpen?.event?.key === item?.key,
                      })}
                      onClick={() => {
                        setEditModalOpen({}, () => {
                          setEditModalOpen(
                            { isOpen: true, event: item, data: addedSettings },
                            () => {
                              // console.log("model closed")
                            }
                          );
                        });
                      }}
                    >
                      <td>
                        {convertPascalCaseToWords(item.name)}
                        <span
                          className={classnames({
                            'dot ml-2': true,
                            'dot--green': !!addedSettings,
                            'dot--red': !addedSettings,
                          })}
                        ></span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
      <div className="col-lg col-md-6 col-12 p-2 setting-detail-block left-0 top-0 bg-white w-100 h-100 zIndex-1">
        {editModalOpen?.isOpen && (
          <>
            <div className="text-right d-md-none">
              <i className="icon-close"></i>
            </div>
            <UpdateForm
              onSubmit={(formData) => {
                const entity = props?.data?.appNotifications
                  ? [...props?.data?.appNotifications]
                  : [];
                const existing = entity?.find(
                  (x) => x.event === formData?.event
                );
                const updateData: AppNotificationSettings[] = entity?.map(
                  (x) => {
                    return {
                      event:
                        existing?.event === x.event ? formData.event : x.event,
                      enabled:
                        existing?.event === x.event
                          ? formData.enabled
                          : x.enabled,
                      notifySalesPerson:
                        existing?.event === x.event
                          ? formData.notifySalesPerson
                          : x.notifySalesPerson,
                      notifyCrmAgent:
                        existing?.event === x.event
                          ? formData.notifyCrmAgent
                          : x.notifyCrmAgent,
                      notifyCustomer:
                        existing?.event === x.event
                          ? formData.notifyCustomer
                          : x.notifyCustomer,
                      whatsappCustomer:
                        existing?.event === x.event
                          ? formData.whatsappCustomer
                          : x.whatsappCustomer,
                      whatsAppTemplateInfo:
                        existing?.event === x.event
                          ? formData.whatsAppTemplateInfo
                          : x.whatsAppTemplateInfo,
                      receiverUserTypes:
                        existing?.event === x.event
                          ? formData.receiverUserTypes
                          : x.receiverUserTypes,
                    };
                  }
                );

                if (!existing) {
                  updateData.push(formData);
                }

                update(updateData);
              }}
              isSubmitting={updating}
              error={updateError?.message}
              data={editModalOpen.data}
              onCancel={() =>
                setEditModalOpen({}, () => {
                  // console.log("model closed")
                })
              }
              event={editModalOpen.event}
            ></UpdateForm>
          </>
        )}
      </div>
    </>
  );
};

export default AppNotificationSettingsTab;
